import React from "react";
import { motion } from "framer-motion";
import { Users, MessageCircleQuestion, BookOpen, ClipboardCheck, Sparkles, Heart } from "lucide-react";

const cards = [
  {
    icon: Users,
    title: "Mentors who actually teach.",
    body: "Every batch capped at 18 students. Faculty hired only after 4 rounds of teaching demos.",
    span: "md:col-span-7 md:row-span-2",
    bg: "#FDF3E7",
    accent: "#FCA311",
  },
  {
    icon: MessageCircleQuestion,
    title: "Daily doubt window.",
    body: "30 mins after every class — no question is small.",
    span: "md:col-span-5",
    bg: "#F3F7F0",
    accent: "#4ECDC4",
  },
  {
    icon: ClipboardCheck,
    title: "Weekly mock tests.",
    body: "Performance dashboards shared with parents every Sunday.",
    span: "md:col-span-5",
    bg: "#FFFFFF",
    accent: "#FF6B6B",
  },
  {
    icon: BookOpen,
    title: "Hand-crafted notes.",
    body: "Every grade gets its own playbook — no recycled photocopies.",
    span: "md:col-span-4",
    bg: "#F3F7F0",
    accent: "#0B132B",
  },
  {
    icon: Sparkles,
    title: "Olympiad lab.",
    body: "Optional weekend coaching for NTSE, KVPY, IMO and more.",
    span: "md:col-span-4",
    bg: "#FFFFFF",
    accent: "#FCA311",
  },
  {
    icon: Heart,
    title: "Care that lasts.",
    body: "Counsellors on call. Parent-teacher syncs every month.",
    span: "md:col-span-4",
    bg: "#FDF3E7",
    accent: "#FF6B6B",
  },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      data-testid="why-choose-us"
      className="px-6 md:px-12 lg:px-16 py-24 md:py-32"
      style={{ background: "var(--bg-secondary)" }}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#FF6B6B] mb-4">
              ✦ Why parents pick us
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-[#0B132B] tracking-tighter leading-[0.95]">
              Six small things <br />we obsess over.
            </h2>
          </div>
          <p className="md:col-span-5 text-lg text-[#3A506B] leading-relaxed self-end">
            We don't believe in one-size-fits-all coaching. Here's what we build into every classroom — from grade 1 to grade 12.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: "easeOut" }}
                whileHover={{ y: -4 }}
                data-testid={`why-card-${i}`}
                className={`${c.span} rounded-3xl p-8 md:p-10 border border-[#0B132B]/5 shadow-[0_14px_40px_rgba(11,19,43,0.10)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(11,19,43,0.18)]`}
                style={{ background: c.bg }}
              >
                <div
                  className="w-12 h-12 rounded-2xl grid place-items-center mb-6"
                  style={{ background: c.accent, color: "#fff" }}
                >
                  <Icon size={20} strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-[#0B132B] mb-3">
                  {c.title}
                </h3>
                <p className="text-[#3A506B] leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}